import * as React from "react";
import { useState } from "react";
import { Autocomplete } from "@mui/material";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { Box } from "@mui/system";
import { ErrorRounded, KeyboardRounded } from "@mui/icons-material";
import {AutocompleteRenderInputParams} from "@mui/material/Autocomplete/Autocomplete";

enum STATUS {
    WAITING, LOADING, DONE
}

interface IAsyncAutocompleteProps {
    id: string,
    value: any,
    otherFilters?: string
    callback: (search: string, id: string, otherFilters: string | undefined) => Promise<any>,
    onChange: (value: any) => void,
    renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode,
    multiple ?:boolean
}

const AsyncAutocomplete = (props: IAsyncAutocompleteProps) => {

    const { t } = useTranslation('salesmarketing');

    const [delay, setDelay] = useState<NodeJS.Timeout | undefined>(undefined);
    const [status, setStatus] = useState(STATUS.WAITING);
    const [options, setOptions] = useState<readonly any[]>([]);

    function getEmptyDesign() {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ErrorRounded sx={{ marginRight: '10px' }}/>
                {t('autocomplete.no-results')}
            </Box>
        );
    }

    function getWaitingDesign() {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <KeyboardRounded sx={{ marginRight: '10px' }}/>
                {t('autocomplete.waiting')}
            </Box>
        );
    }

    function getLoadingDesign() {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress color="inherit" size={'2ch'} sx={{ marginRight: '10px' }}/>
                {t('autocomplete.loading')}
            </Box>
        );
    }

    return (
        <Autocomplete
            multiple = {props.multiple}
            clearOnEscape
            id={props.id}
            value={props.value}
            renderInput={props.renderInput}
            loading={status !== STATUS.DONE}
            loadingText={status === STATUS.LOADING ? getLoadingDesign() : getWaitingDesign()}
            noOptionsText={getEmptyDesign()}
            options={options}
            isOptionEqualToValue={(option, value) => {
                if (option.hasOwnProperty("value")) {
                    return option.value === value.value;
                }
                return option === value;
            }}
            getOptionLabel={(option) => {
                if (option.hasOwnProperty("label")) {
                    return option.label;
                }
                return option;
            }}
            onClose={() => {
                setStatus(STATUS.WAITING);
            }}
            onChange={(event, value) => props.onChange(value)}
            onInputChange={(event: React.SyntheticEvent, value: string, id: string) => {
                if (delay !== null) {
                    clearTimeout(delay);
                }
                if (value.trim().length === 0) {
                    setOptions([]);
                    setStatus(STATUS.WAITING)
                    return;
                }
                setStatus(STATUS.LOADING)
                setDelay(setTimeout(() => {
                    props.callback(value, props.id, props.otherFilters).then(it => {
                        setOptions(it);
                        setStatus(STATUS.DONE);
                    })
                }, 850));
            }}
        />
    )
}

export default AsyncAutocomplete;